import {
  Box,
  CollapsableCard,
  Hint,
  Icon,
  Text,
  TogglerCheckbox,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { FormikProps } from 'formik'
import { Features } from 'generated/featureDefinitions'
import { UpdateAttendanceSettingMutationVariables } from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import SelectField from 'modules/core/components/SelectField'
import { Return } from 'modules/core/lib/asyncAction'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useTimeTrackingFeatureAvailability } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

import EntranceAppCheckbox from './EntranceAppCheckbox'
import FaceRecognitionToggle from './FaceRecognitionToggle'
import IdClockSharedDeviceToggle from './IdClockSharedDeviceToggle'

interface MobileSettingsProp {
  policyForm: FormikProps<UpdateAttendanceSettingMutationVariables>
  updatePolicy: (value?: { successFlash: boolean }) => Promise<Return<void>>
  attendanceSetting: AttendanceSetting
}
type Props = MobileSettingsProp
type DesktopClockInOutProps = Omit<MobileSettingsProp, 'attendanceSetting'>

const MobileSettings = ({ policyForm, updatePolicy, attendanceSetting }: MobileSettingsProp) => {
  const [open, toggle] = useToggle(false)
  const [hideMultiLocationHint, setHideMultiLocationHint] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideMultiLocationHintAttendanceSetting
  )

  const { featureAvailability: hasGeofencingFeature } = useAttendanceFeatures(
    Features.time_tracking_geofencing_alerts
  )

  const handleOnCloseHint = () => {
    setHideMultiLocationHint(true)
  }

  const handleGeolocationToggle = (geoStatus: boolean) => {
    if (geoStatus) {
      policyForm.setValues({
        ...policyForm.values,
        geolocation: geoStatus,
        qrCodeClockin: false,
      })
    } else {
      policyForm.setValues({
        ...policyForm.values,
        geolocation: geoStatus,
        geofencing: false,
        geolocationBlock: false,
      })
    }

    updatePolicy({ successFlash: false })
  }

  const hasGeoBlockingFeature = hasFeature('dev_time_tracking_geofencing_block')

  const geolocationValue = policyForm.values.geofencing
    ? 'alert'
    : policyForm.values.geolocationBlock
    ? 'block'
    : 'geolocation'

  const geolocationOptions = [
    {
      value: 'geolocation',
      label: i18n.t('time_tracking.settings.form.toggle.geofencing_incident.options.geolocation'),
    },
  ]
  if (hasGeofencingFeature)
    geolocationOptions.push({
      value: 'alert',
      label: i18n.t('time_tracking.settings.form.toggle.geofencing_incident.options.alert'),
    })
  if (hasGeoBlockingFeature)
    geolocationOptions.push({
      value: 'block',
      label: i18n.t('time_tracking.settings.form.toggle.geofencing_incident.options.block'),
    })

  return (
    <Box gap="s32">
      <Box gap="s12">
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          extra={{ 'data-intercom-target': 'attendance-policy-details-toggles-geolocation' }}
        >
          <Text.Normal weight="medium">
            {i18n.t('time_tracking.settings.form.toggle.geolocation.title')}
          </Text.Normal>
          <TogglerCheckbox
            value={!!policyForm.values.geolocation}
            onChange={handleGeolocationToggle}
          />
        </Box>
        <Text.Normal color="grey800" htmlTag="div">
          {i18n.t('time_tracking.settings.form.toggle.geolocation.description')}
        </Text.Normal>
        <HelpCenterLink name="geolocation_clock_in">
          {i18n.t('time_tracking.settings.form.toggle.geolocation.link')}
        </HelpCenterLink>
        {(hasGeofencingFeature || hasGeoBlockingFeature) && policyForm.values.geolocation && (
          <>
            <SelectField
              label={i18n.t('time_tracking.settings.form.toggle.geofencing_incident.title')}
              value={geolocationValue}
              secondaryLabel={
                <Tooltip.Alt
                  attachment="bottom center"
                  open={open}
                  toggle={toggle}
                  label={i18n.t('time_tracking.settings.form.toggle.geofencing_incident.hint')}
                >
                  <Icon.Small icon="alert" encircled="grey800" />
                </Tooltip.Alt>
              }
              options={geolocationOptions}
              onChange={(value) => {
                policyForm.setValues({
                  ...policyForm.values,
                  geofencing: value === 'alert',
                  geolocationBlock: value === 'block',
                })
                updatePolicy({
                  successFlash: false,
                })
              }}
              mandatory
            />
            {(policyForm.values.geofencing || policyForm.values.geolocationBlock) && (
              <Box>
                <Hint.Popover.Provider steps={1} onClose={handleOnCloseHint}>
                  <Hint.Popover
                    step={1}
                    title={i18n.t('time_tracking.settings.form.hint.multiple_location.title')}
                    description={i18n.t(
                      'time_tracking.settings.form.hint.multiple_location.description'
                    )}
                    actionLabel={i18n.t('common.close')}
                    position="bottom right"
                    isHidden={!!hideMultiLocationHint}
                  ></Hint.Popover>
                </Hint.Popover.Provider>
              </Box>
            )}
          </>
        )}
      </Box>
      <EntranceAppCheckbox
        policyForm={policyForm}
        updatePolicy={updatePolicy}
        attendanceSetting={attendanceSetting}
      />
    </Box>
  )
}

const DesktopClockInOut = ({ policyForm, updatePolicy }: DesktopClockInOutProps) => {
  const desktopClockInField = policyForm.values.desktopClockin

  const onChange = (status: boolean) => {
    policyForm.setFieldValue('desktopClockin', status)
    updatePolicy({
      successFlash: false,
    })
  }

  return (
    <Box gap="s8" extra={{ 'data-intercom-target': 'attendance-policy-details-toggles-desktop' }}>
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text.Normal weight="medium">
          {i18n.t('time_tracking.settings.form.toggle.desktop.title')}
        </Text.Normal>
        <TogglerCheckbox
          value={!!desktopClockInField}
          onChange={(newValue) => onChange(newValue)}
          labelBefore
        />
      </Box>
      <Text.Normal color="grey800" htmlTag="div">
        {i18n.t('time_tracking.settings.form.toggle.desktop.description')}
      </Text.Normal>
    </Box>
  )
}

const Toggles = ({ policyForm, updatePolicy, attendanceSetting }: Props) => {
  const faceRecognitionEnabled = useTimeTrackingFeatureAvailability(
    'time_tracking_face_recognition'
  )
  const mobileClockinField = policyForm.values.mobileClockin

  const handleMobileToggle = (mobileStatus: boolean) => {
    policyForm.setFieldValue('mobileClockin', mobileStatus)

    if (!mobileStatus) {
      policyForm.setValues({
        ...policyForm.values,
        mobileClockin: mobileStatus,
        geolocation: false,
        geofencing: false,
        geolocationBlock: false,
        qrCodeClockin: false,
      })
    }

    updatePolicy({
      successFlash: false,
    })
  }

  return (
    <Box gap="s16">
      <Box
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
        borderRadius={{
          all: 'abs008',
        }}
        padding="s16"
      >
        <DesktopClockInOut policyForm={policyForm} updatePolicy={updatePolicy} />
      </Box>
      <CollapsableCard
        open={!!mobileClockinField}
        Card={
          <Box
            gap="s8"
            extra={{ 'data-intercom-target': 'attendance-policy-details-toggles-mobile' }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="medium">
                {i18n.t('time_tracking.settings.form.toggle.mobile.title')}
              </Text.Normal>
              <TogglerCheckbox
                value={!!mobileClockinField}
                onChange={(newValue) => handleMobileToggle(newValue)}
                labelBefore
              />
            </Box>
            <Text.Normal color="grey800" htmlTag="div">
              {i18n.t('time_tracking.settings.form.toggle.mobile.description')}
            </Text.Normal>
          </Box>
        }
        Content={
          <Box gap="s8">
            <MobileSettings
              policyForm={policyForm}
              updatePolicy={updatePolicy}
              attendanceSetting={attendanceSetting}
            />
          </Box>
        }
      />
      {faceRecognitionEnabled && (
        <FaceRecognitionToggle
          showFaceRecognitionSettings={!!policyForm.values.faceRecognition}
          policyForm={policyForm}
          updatePolicy={updatePolicy}
          attendanceSetting={attendanceSetting}
        />
      )}
      <IdClockSharedDeviceToggle
        policyForm={policyForm}
        updatePolicy={updatePolicy}
        attendanceSetting={attendanceSetting}
      />
    </Box>
  )
}

export default Toggles
